import React, { cloneElement, isValidElement } from 'react';
import isObject from 'lodash/isObject';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import isNumber from 'lodash/isNumber';
import defaultTo from 'lodash/defaultTo';
import isPlainObject from 'lodash/isPlainObject';
import { get, isArrayLike, isObservableObject } from 'mobx';
import classNames from 'classnames';
import moment, { isMoment } from 'moment';
import { getTooltipTheme } from 'choerodon-ui/lib/_util/TooltipUtils';
import { getConfig, getProPrefixCls } from 'choerodon-ui/lib/configure';
import { FieldType, RecordStatus } from '../data-set/enum';
import formatCurrency from '../formatter/formatCurrency';
import formatNumber from '../formatter/formatNumber';
import { getPrecision } from '../number-field/utils';
import isEmpty from '../_util/isEmpty';
import CloseButton from './CloseButton';
import { hide, show } from '../tooltip/singleton';
import Icon from '../icon';
import { $l } from '../locale-context';
import isReactChildren from '../_util/isReactChildren';
import { defaultTextField, findBindFields } from '../data-set/utils';
import * as ObjectChainValue from '../_util/ObjectChainValue';
import MultiLine from '../output/MultiLine';
export function toRangeValue(value, range) {
    if (isArrayLike(range)) {
        if (isObservableObject(value)) {
            return [get(value, range[0]), get(value, range[1])];
        }
        if (isObject(value)) {
            return [value[range[0]], value[range[1]]];
        }
    }
    else if (isArrayLike(value)) {
        return value.slice(0, 2);
    }
    return [undefined, undefined];
}
export function fromRangeValue(value, range) {
    if (isArrayLike(range)) {
        const [start, end] = range;
        return {
            [start]: value[0],
            [end]: value[1],
        };
    }
    return value;
}
export function toMultipleValue(value, range) {
    if (!isNil(value)) {
        const multipleValue = isArrayLike(value) ? value.slice() : [value];
        if (range) {
            return multipleValue.map(item => toRangeValue(item, range));
        }
        return multipleValue;
    }
    return [];
}
export function getDateFormatByFieldType(type) {
    const formatter = getConfig('formatter');
    switch (type) {
        case FieldType.date:
            return formatter.date;
        case FieldType.dateTime:
            return formatter.dateTime;
        case FieldType.week:
            return formatter.week;
        case FieldType.month:
            return formatter.month;
        case FieldType.year:
            return formatter.year;
        case FieldType.time:
            return formatter.time;
        default:
            return formatter.date;
    }
}
export function getDateFormatByField(field, type) {
    if (field) {
        return field.get('format') || getDateFormatByFieldType(type || field.type);
    }
    if (type) {
        return getDateFormatByFieldType(type);
    }
    return getConfig('formatter').jsonDate || moment.defaultFormat;
}
export function transformHighlightProps(highlight, props) {
    if (isValidElement(highlight) || isString(highlight)) {
        return {
            ...props,
            content: highlight,
        };
    }
    if (isObject(highlight)) {
        return {
            ...props,
            ...highlight,
        };
    }
    return props;
}
export function getCurrencyFormatter() {
    const currencyFormatter = getConfig('currencyFormatter');
    if (currencyFormatter !== undefined) {
        return currencyFormatter;
    }
    return formatCurrency;
}
export function getNumberFormatter() {
    const numberFieldFormatter = getConfig('numberFieldFormatter');
    if (numberFieldFormatter !== undefined) {
        return numberFieldFormatter;
    }
    return formatNumber;
}
export function getCurrencyFormatOptions(getProp, controlLang) {
    const precision = getProp('precision');
    const formatterOptions = getProp('formatterOptions') || {};
    const currencyFormatterOptions = getConfig('currencyFormatterOptions') || { options: {} };
    const lang = formatterOptions.lang || currencyFormatterOptions.lang || controlLang;
    const options = {};
    if (isNumber(precision)) {
        options.minimumFractionDigits = precision;
        options.maximumFractionDigits = precision;
    }
    Object.assign(options, currencyFormatterOptions.options, formatterOptions.options);
    const numberGrouping = getProp('numberGrouping');
    const currency = getProp('currency');
    if (currency) {
        options.currency = currency;
    }
    if (numberGrouping === false) {
        options.useGrouping = false;
    }
    return {
        lang,
        options,
    };
}
export function getNumberFormatOptions(getProp, getValue, value, controlLang) {
    const precision = getProp('precision');
    const precisionInValue = isNumber(precision) ? precision : getPrecision(isNil(value) ? getValue ? getValue() || 0 : 0 : value);
    const formatterOptions = getProp('formatterOptions') || {};
    const numberFieldFormatterOptions = getConfig('numberFieldFormatterOptions') || { options: {} };
    const lang = formatterOptions.lang || numberFieldFormatterOptions.lang || controlLang;
    const options = {
        maximumFractionDigits: precisionInValue,
        ...numberFieldFormatterOptions.options,
        ...formatterOptions.options,
    };
    const numberGrouping = getProp('numberGrouping');
    if (numberGrouping === false) {
        options.useGrouping = false;
    }
    return {
        lang,
        options,
    };
}
export function processFieldValue(value, field, options, showValueIfNotFound) {
    const { getProp, getValue, lang } = options;
    const type = field && field.type;
    const currency = getProp('currency');
    if (currency || type === FieldType.currency) {
        const formatOptions = getCurrencyFormatOptions(getProp, lang);
        const formatter = getProp('formatter');
        return (formatter || getCurrencyFormatter())(value, formatOptions.lang, formatOptions.options);
    }
    if (type === FieldType.number) {
        const formatOptions = getNumberFormatOptions(getProp, getValue, value, lang);
        const formatter = getProp('formatter');
        return (formatter || getNumberFormatter())(value, formatOptions.lang, formatOptions.options);
    }
    if (field) {
        return field.getText(value, showValueIfNotFound);
    }
    return value;
}
export function processValue(value, format) {
    if (!isNil(value)) {
        if (isMoment(value)) {
            if (value.isValid()) {
                return value.format(format);
            }
            if (getConfig('showInvalidDate')) {
                return $l('DatePicker', 'invalid_date');
            }
            return '';
        }
        if (isReactChildren(value)) {
            // For Select's Option and TreeSelect's TreeNode which type may be ReactElement
            // @ts-ignore
            return value;
        }
        return value.toString();
    }
    return '';
}
export function isFieldValueEmpty(value, range, multiple, valueField, textField) {
    if (isEmpty(value)) {
        return true;
    }
    if (multiple) {
        return !isArrayLike(value) || value.length === 0;
    }
    if (range === true) {
        return isArrayLike(value) && value.every(v => isEmpty(v));
    }
    if (isArrayLike(range)) {
        return value && Object.values(value).every(v => isEmpty(v));
    }
    const isObjectEmpty = (v) => {
        if (valueField && textField) {
            if (isObservableObject(v)) {
                return isEmpty(get(v, valueField)) && isEmpty(get(v, textField));
            }
            if (isObject(v)) {
                return isEmpty(v[valueField]) && isEmpty(v[textField]);
            }
        }
        return false;
    };
    if (isArrayLike(value)) {
        return value.length ? value.every(v => {
            if (isEmpty(v)) {
                return true;
            }
            return isObjectEmpty(v);
        }) : true;
    }
    return isObjectEmpty(value);
}
export function renderRangeValue(value, option) {
    const { repeat, processRenderer } = option;
    const rangeValue = (value || []).map((item) => processRenderer(item, repeat), []);
    if (rangeValue.some(v => !isEmpty(v))) {
        return (React.createElement(React.Fragment, null,
            rangeValue[0],
            "~",
            rangeValue[1]));
    }
}
export function getValueKey(v) {
    if (isArrayLike(v)) {
        return v.join(',');
    }
    return v;
}
export function renderMultipleValues(value, option) {
    const { range, maxTagPlaceholder, prefixCls, validator, disabled, readOnly, isMultipleBlockDisabled, processRenderer, renderValidationResult, handleMutipleValueRemove, getKey = getValueKey, isValidationMessageHidden, showValidationMessage: selfShowValidationMessage, } = option;
    const values = toMultipleValue(value, range);
    const valueLength = values.length;
    const { maxTagCount = valueLength } = option;
    const repeats = new Map();
    const blockClassName = classNames({
        [`${prefixCls}-multiple-block-disabled`]: disabled,
    }, `${prefixCls}-multiple-block`);
    let multipleValidateMessageLength = 0;
    const validationResults = validator && validator.validationResults;
    const tags = values.slice(0, maxTagCount).map((v, index) => {
        const key = getKey(v);
        const repeat = repeats.get(key) || 0;
        const text = range ? renderRangeValue(v, { repeat, processRenderer }) : processRenderer(v, repeat);
        repeats.set(key, repeat + 1);
        if (!isEmpty(text)) {
            const validationResult = validationResults && validationResults.find(error => error.value === v);
            const blockDisabled = isMultipleBlockDisabled ? isMultipleBlockDisabled(v) : disabled;
            const className = classNames({
                [`${prefixCls}-multiple-block-invalid`]: validationResult,
                [`${prefixCls}-multiple-block-disabled`]: blockDisabled,
            }, `${prefixCls}-multiple-block`);
            const validationMessage = validationResult && renderValidationResult(validationResult);
            if (validationMessage) {
                multipleValidateMessageLength++;
            }
            const closeBtn = !blockDisabled && !readOnly && (React.createElement(CloseButton, { onClose: handleMutipleValueRemove, value: v, index: repeat }));
            const inner = readOnly ? (React.createElement("span", { key: String(index), className: className }, text)) : (React.createElement("li", { key: String(index), className: className },
                React.createElement("div", null, text),
                closeBtn));
            if (!isValidationMessageHidden(validationMessage)) {
                return cloneElement(inner, {
                    onMouseEnter: (e) => selfShowValidationMessage(e, validationMessage),
                    onMouseLeave: () => hide(),
                });
            }
            return inner;
        }
        return undefined;
    });
    if (valueLength > maxTagCount) {
        let content = `+ ${valueLength - maxTagCount} ...`;
        if (maxTagPlaceholder) {
            const omittedValues = values.slice(maxTagCount, valueLength);
            content =
                typeof maxTagPlaceholder === 'function'
                    ? maxTagPlaceholder(omittedValues)
                    : maxTagPlaceholder;
        }
        tags.push(React.createElement("li", { key: "maxTagPlaceholder", className: blockClassName },
            React.createElement("div", null, content)));
    }
    return { tags, multipleValidateMessageLength };
}
export function renderMultiLine(options) {
    const { field, record, dataSet, name, prefixCls, renderer, renderValidationResult, isValidationMessageHidden, processValue: selfProcessValue, labelTooltip, tooltip, } = options;
    let multipleValidateMessageLength = 0;
    if (record) {
        const multiLineFields = findBindFields(field, record.fields);
        if (renderer) {
            return {
                lines: renderer({
                    multiLineFields,
                    record,
                    dataSet,
                    name,
                }),
                multipleValidateMessageLength,
            };
        }
        if (multiLineFields.length) {
            const lines = (multiLineFields.map(fieldItem => {
                if (fieldItem) {
                    const { validator } = fieldItem;
                    const required = defaultTo(fieldItem.get('required'), field.get('required'));
                    const fieldName = fieldItem.get('name');
                    const value = record.get(fieldName);
                    const validationResult = validator && validator.validationResults.find(error => error.value === value);
                    const validationMessage = validationResult && renderValidationResult(validationResult);
                    const validationHidden = isValidationMessageHidden(validationMessage);
                    let processedValue = '';
                    if (fieldItem.get('lovCode')) {
                        const fieldValue = fieldItem.getValue();
                        if (isPlainObject(fieldValue)) {
                            processedValue = ObjectChainValue.get(fieldValue, fieldItem.get('textField') || defaultTextField);
                        }
                    }
                    const notEmpty = !isEmpty(value);
                    // 值集中不存在 再去取直接返回的值
                    const text = processedValue || selfProcessValue(value);
                    multipleValidateMessageLength++;
                    const validationInner = notEmpty ? text :
                        validationHidden ? record.status === RecordStatus.add ? '' :
                            React.createElement("span", { className: `${prefixCls}-multi-value-invalid` }, text) : validationMessage;
                    const label = fieldItem.get('label');
                    return (React.createElement(MultiLine, { key: `${record.index}-multi-${fieldName}`, prefixCls: prefixCls, label: label, required: required, validationMessage: validationMessage, validationHidden: validationHidden, tooltip: tooltip, labelTooltip: labelTooltip }, validationInner));
                }
                return null;
            }));
            return {
                lines,
                multipleValidateMessageLength,
            };
        }
    }
    return { multipleValidateMessageLength };
}
export function renderValidationMessage(validationMessage, showIcon) {
    if (validationMessage) {
        return (React.createElement("div", { className: getProPrefixCls('validation-message') },
            showIcon && React.createElement(Icon, { type: "error" }),
            React.createElement("span", null, validationMessage)));
    }
}
export function defaultRenderer(renderOption) {
    const { text, repeat, maxTagTextLength } = renderOption;
    return repeat !== undefined &&
        maxTagTextLength &&
        isString(text) &&
        text.length > maxTagTextLength
        ? `${text.slice(0, maxTagTextLength)}...`
        : text;
}
export function showValidationMessage(e, message) {
    show(e.currentTarget, {
        suffixCls: `form-tooltip ${getConfig('proPrefixCls')}-tooltip`,
        title: message,
        theme: getTooltipTheme('validation'),
        placement: 'bottomLeft',
    });
}
